import { useEffect, useState } from "react";
import {
  ADS_CAMPAIGN_GOOGLE_BUSINESS_NAME_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_FIRST_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_FIRST_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_MAX_DESCRIPTIONS,
  ADS_CAMPAIGN_GOOGLE_MAX_LONG_HEADLINES,
  ADS_CAMPAIGN_GOOGLE_MAX_TITLES,
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_LONG_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP,
  ALERT_ICON_TYPE_SUCCESS,
  BORDER_RADIUS,
  GOOGLE,
  GOOGLE_IMAGE_FORMATS,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material";
import {
  TITLES,
  DESCRIPTIONS,
  LONG_HEADLINES,
  TITLE_PLACEHOLDER,
  DESCRIPTION_PLACEHOLDER,
  LONG_HEADLINE_PLACEHOLDER,
  IMAGES_OF_CAMPAIGN_PLACEHOLDER,
  VIDEOS_OF_CAMPAIGN_PLACEHOLDER,
  LOGO_URLS_PLACEHOLDER,
  ADS_CAMPAIGN_PLATFORM_BRAND_CONFIGURATION_SUB_TITLE,
  BUSINESS_NAME_PLACEHOLDER,
  ADS_CAMPAIGN_PLATFORM_ASSETS_CONFIGURATION_SUB_TITLE,
  ADS_CAMPAIGN_SEGMENTATION_SUB_TITLE,
  SEARCH_THEMES_PLACEHOLDER,
  ADS_CONFIGURATION_DESTINATION_TITLE,
  DESTINATION_URL_PLACEHOLDER,
  UTM_PARAMETERS_PLACEHOLDER,
  REQUIRED_IMAGES_GOOGLE_ASSETS_GROUP_HELPER_TEXT,
  READY,
  ADS_CAMPAIGN_PRODUCTS_CONFIGURATION_SUB_TITLE,
  COPY_VALUES_IN_OTHER_ADGROUPS,
  BRAND_GUIDELINES_ENABLED_CLARIFICATION,
  REQUIRED_VIDEOS_GOOGLE_ASSETS_GROUP_HELPER_TEXT,
} from "../../../../i18n/keysTranslations";
import { Controller, useFormContext } from "react-hook-form";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { useTranslationApp } from "../../../../lib/i18next";
import { waitDelay } from "../../../../utils/date";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "../../../Form/TextField";
import MultiContentField from "../../../Form/MultiContentField";
import Accordion from "../../../Containers/Accordion";
import TooltipOptimization from "../../../Tooltip/TooltipOptimization";
import {
  getReactHookFormNestedError,
  getFirstNestedFieldErrorMessage,
} from "../../../../utils/errors";
import { getUniqueID } from "../../../../utils/numbers";
import { useDispatchApp } from "../../../../lib/redux";
import { generateAdsCampaignDescription } from "../../../../actions/adsCampaigns";
import SelectCreatable from "../../../Form/SelectCreatable";
import ContainerVideosYoutube from "./ContainerVideosYoutube";
import UploadYoutubeVideoLink from "./UploadYoutubeVideoLink";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";
import { getIsBlockedActions } from "../../../../actions/getters";
import ProductsConfigurationContainerGoogle from "./ProductsConfiguration/ProductsConfigurationContainerGoogle";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";
import CustomAudiencesContainerGoogle from "../Common/CustomAudiencesContainerGoogle";
import CardContainer from "../../../Card/CardContainer";
import CardContainerWithCopy from "../../../Card/CardContainerWithCopy";
import ChipOptional from "../../../Chip/ChipOptional";
import TextFieldWithActions from "../../../Form/TextFieldWithActions";
import _ from "lodash";
import {
  buildCreativeOptimizationPrompt,
  getCreativeOptimizationEntryForSlot,
} from "../../../../utils/creativeOptimizationContext";
import UtmParametersContainer from "../../../Common/Utms/UtmParametersContainer";

const platform = GOOGLE;
const grid = {
  xs: 12,
  lg: 6,
};

const commonSxFields = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

const commonSxAccordion = {
  backgroundColor: "fields.background",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: BOX_SHADOW_FIELDS,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "stretch",
  minHeight: 70,
};

const AssetsGroupMediaSection = ({
  children,
  title,
  helperText = "",
  name,
  id,
  errors,
  showTooltip = false,
  titleTooltip = "",
  actionTooltip = "",
}) => {
  const theme = useTheme();
  const error = getReactHookFormNestedError(errors, name);
  const hasError = Boolean(
    error?.message || getFirstNestedFieldErrorMessage(error)
  );

  return (
    <>
      <TooltipOptimization
        isOpen={showTooltip}
        titleTooltip={titleTooltip}
        actionTooltip={actionTooltip}
      >
        <Box
          id={id}
          data-field={name || id}
          sx={{
            borderRadius: "10px",
            overflow: "hidden",
            ...commonSxAccordion,
            border: `${
              hasError ? theme.palette.error.main : INPUT_COLOR_BORDER
            } 1px solid`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: helperText ? "auto" : 70,
              pt: helperText ? 1.5 : 0,
              pb: helperText ? 1 : 0,
              px: 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "1.05rem", fontWeight: 600 }}
            >
              {title}
            </Typography>
            {helperText ? (
              <Typography
                variant="body2"
                sx={{ mt: 0.5 }}
                dangerouslySetInnerHTML={{ __html: helperText }}
              />
            ) : null}
          </Box>
          <Box sx={{ px: 2, pb: 2 }}>{children}</Box>
        </Box>
      </TooltipOptimization>
    </>
  );
};

const AssetsGroupGoogleForm = () => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    control,
    formState,
    watch,
    sectionSelected,
    setValue,
    getValues,
    isGeneralDisabledFields,
    trigger,
    creativeOptimizations,
    onRemoveCreativeOptimization,
  } = useFormContext();
  useFocusErrorForm(formState);
  const dispatch = useDispatchApp();

  const errors = formState.errors;
  const { t } = useTranslationApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();

  const language = watch("language");
  const platformsData = watch("platformsData") || {};
  const platformData = platformsData?.[platform] || {};
  const merchantCenterID = platformData?.merchantCenterID;
  const isIncompatibleCampaign = platformData?.isIncompatible;
  const brandGuidelinesEnabled = platformData?.brandGuidelinesEnabled;
  const assetsGroups = watch("assetsGroups");
  const assetGroupID = sectionSelected.assetsGroupID;
  const index = assetsGroups.findIndex((item) => item.id === assetGroupID);
  const BASIC_PATH = `assetsGroups.[${index}]`;
  const isIncompatibleAssetsGroup =
    getValues(`${BASIC_PATH}.isIncompatible`) || false;

  const isDisabledFields =
    isGeneralDisabledFields ||
    isIncompatibleCampaign ||
    isIncompatibleAssetsGroup;

  const titles = watch(`${BASIC_PATH}.titles`) || [
    { value: "", id: getUniqueID() },
  ];
  const descriptions = watch(`${BASIC_PATH}.descriptions`) || [
    { value: "", id: getUniqueID() },
  ];
  const longHeadlines = watch(`${BASIC_PATH}.longHeadlines`) || [
    { value: "", id: getUniqueID() },
  ];

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await waitDelay(1);
      setIsLoading(false);
    })();
  }, [sectionSelected]);

  const onAddText = ({ type }) => {
    const currentTexts = watch(`${BASIC_PATH}.${type}`) || [];
    setValue(`${BASIC_PATH}.${type}`, [
      ...currentTexts,
      { value: "", id: getUniqueID() },
    ]);
  };

  const onRemoveText = ({ type, index }) => {
    const currentTexts = watch(`${BASIC_PATH}.${type}`);
    setValue(
      `${BASIC_PATH}.${type}`,
      currentTexts.filter((_, i) => i !== index)
    );
    trigger(`${BASIC_PATH}.${type}`);
  };

  const onGenerateText = async ({ type, index }) => {
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
        showUsageConfirmationAlert: true,
      })
    );
    if (isBlocked) return;

    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    const currentText = currentTexts[index]?.value || "";
    const allTexts = currentTexts.map((item) => item.value || "");
    const textsWithCurrentFirst = [
      currentText,
      ...allTexts.filter((_, i) => i !== index),
    ];

    const objective = getValues(`platformsData.${platform}.objective`);
    const response = await dispatch(
      generateAdsCampaignDescription({
        objective,
        platform,
        textIndex: index,
        campaignContext: getValues("context") || "",
        language: getValues("language"),
        destinationUrl: getValues(`${BASIC_PATH}.destinationUrl`) || "",
        type,
        currentTexts: textsWithCurrentFirst,
      })
    );

    if (response) {
      setValue(`${BASIC_PATH}.${type}.[${index}].value`, response);
      trigger(`${BASIC_PATH}.${type}`);
    }
  };

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    const assetsGroups = getValues(`assetsGroups`);

    assetsGroups.forEach((assetsGroup, currentIndexAssetsGroup) => {
      if (assetsGroup.platform !== platform) return;

      setValue(`assetsGroups.[${currentIndexAssetsGroup}].${type}`, value);
    });
    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };

  const pickGoogleAssetsCreativeEntry = (primaryField) =>
    getCreativeOptimizationEntryForSlot({
      creativeOptimizations,
      formPath: BASIC_PATH,
      field: primaryField,
    });

  const creativeEntryImage = pickGoogleAssetsCreativeEntry("imageUrls");
  const creativeEntryVideo = pickGoogleAssetsCreativeEntry("videoUrls");

  useEffect(() => {
    const ent = creativeEntryImage || creativeEntryVideo;
    if (!ent?.title) return;
    const scrollSuffix = ent.field;
    document
      .getElementById(`${BASIC_PATH}.${scrollSuffix}`)
      ?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line
  }, [creativeEntryImage?.slotKey, creativeEntryVideo?.slotKey, BASIC_PATH]);

  const showTooltipImageUrls = !_.isEmpty(creativeEntryImage);
  const showTooltipVideoUrls = !_.isEmpty(creativeEntryVideo);

  if (isLoading) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
      }}
    >
      <Grid container spacing={2}>
        {/* Destination URL */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }} variant="h6">
                  {t(ADS_CONFIGURATION_DESTINATION_TITLE)}
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={commonSxFields}>
                  <Typography variant="body1">
                    {t(DESTINATION_URL_PLACEHOLDER)}
                  </Typography>
                  <TextField
                    name={`${BASIC_PATH}.destinationUrl`}
                    variant="filled"
                    fullWidth
                    disabled={isDisabledFields}
                    control={control}
                    errors={errors}
                  />
                  <ClickableText
                    disabled={isDisabledFields}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "destinationUrl" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
                  </ClickableText>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body1">
                      {t(UTM_PARAMETERS_PLACEHOLDER)}
                    </Typography>
                    <ChipOptional size="small" />
                  </Box>
                  <UtmParametersContainer
                    name={`${BASIC_PATH}.utmParameters`}
                    control={control}
                    errors={errors}
                    disabled={isDisabledFields}
                    platform={platform}
                    googleUtmEntityFormPath={BASIC_PATH}
                    onCopyValuesInOtherAds={() =>
                      onCopyValuesInOtherAds({ type: "utmParameters" })
                    }
                    copyText={COPY_VALUES_IN_OTHER_ADGROUPS}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>

        {/* Products Configuration */}
        {merchantCenterID && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(ADS_CAMPAIGN_PRODUCTS_CONFIGURATION_SUB_TITLE)}
                  </Typography>
                </Box>
              }
            >
              <Grid item xs={12}>
                <ProductsConfigurationContainerGoogle
                  integrationID={platformData?.integrationID}
                  adAccountID={platformData?.adAccountID}
                  merchantCenterID={merchantCenterID}
                  name={`${BASIC_PATH}.productsConfiguration`}
                  disabled={isDisabledFields}
                  control={control}
                  errors={errors}
                />
              </Grid>
            </CardContainer>
          </Grid>
        )}

        {/* Brand Configuration */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(ADS_CAMPAIGN_PLATFORM_BRAND_CONFIGURATION_SUB_TITLE)}
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item {...grid}>
                <Typography variant="body1">
                  {t(BUSINESS_NAME_PLACEHOLDER)}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <ClickableText
                    disabled={isDisabledFields || brandGuidelinesEnabled}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "businessName" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
                  </ClickableText>
                </Box>
                <TextField
                  name={`${BASIC_PATH}.businessName`}
                  disabled={isDisabledFields || brandGuidelinesEnabled}
                  variant="filled"
                  fullWidth
                  control={control}
                  errors={errors}
                  showCounterCharacters={true}
                  maxCharacters={ADS_CAMPAIGN_GOOGLE_BUSINESS_NAME_MAX_LENGTH}
                />
                {brandGuidelinesEnabled && (
                  <Typography variant="body2" color="fields.placeholder">
                    {t(BRAND_GUIDELINES_ENABLED_CLARIFICATION)}
                  </Typography>
                )}
              </Grid>
              <Grid item {...grid}>
                <Typography variant="body1">
                  {t(LOGO_URLS_PLACEHOLDER)}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <ClickableText
                    disabled={isDisabledFields || brandGuidelinesEnabled}
                    onClick={() => onCopyValuesInOtherAds({ type: "logoUrls" })}
                  >
                    {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
                  </ClickableText>
                </Box>
                <Accordion
                  title={t(LOGO_URLS_PLACEHOLDER)}
                  sx={{
                    ...commonSxAccordion,
                  }}
                  name={`${BASIC_PATH}.logoUrls`}
                  id={`${BASIC_PATH}.logoUrls`}
                  errors={errors}
                  showError={true}
                >
                  <Controller
                    control={control}
                    name={`${BASIC_PATH}.logoUrls`}
                    id={`${BASIC_PATH}.logoUrls`}
                    render={({ field }) => (
                      <MultiContentField
                        platform={GOOGLE}
                        language={language}
                        disabled={isDisabledFields || brandGuidelinesEnabled}
                        acceptedFormats={GOOGLE_IMAGE_FORMATS}
                        fieldPath={`${BASIC_PATH}.logoUrls`}
                        mediaUrls={field.value}
                        onChange={(contents) => {
                          const newLogoUrls = contents.map(
                            (content) => content.url
                          );
                          const newValues =
                            getValues(`${BASIC_PATH}.logoUrls`) || [];
                          field.onChange([...newValues, ...newLogoUrls]);
                        }}
                        onDelete={(index) => {
                          field.onChange(
                            field.value.filter((_, i) => i !== index)
                          );
                        }}
                      />
                    )}
                  />
                </Accordion>
                {brandGuidelinesEnabled && (
                  <Typography
                    variant="body2"
                    color="fields.placeholder"
                    sx={{
                      mt: 1,
                    }}
                  >
                    {t(BRAND_GUIDELINES_ENABLED_CLARIFICATION)}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>

        {/* Media Configuration */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(ADS_CAMPAIGN_PLATFORM_ASSETS_CONFIGURATION_SUB_TITLE)}
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item {...grid}>
                <AssetsGroupMediaSection
                  showTooltip={showTooltipImageUrls}
                  titleTooltip={creativeEntryImage?.title}
                  actionTooltip={creativeEntryImage?.action}
                  title={t(IMAGES_OF_CAMPAIGN_PLACEHOLDER)}
                  helperText={t(
                    REQUIRED_IMAGES_GOOGLE_ASSETS_GROUP_HELPER_TEXT
                  )}
                  name={`${BASIC_PATH}.imageUrls`}
                  id={`${BASIC_PATH}.imageUrls`}
                  errors={errors}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "imageUrls" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
                    </ClickableText>
                  </Box>
                  <Controller
                    control={control}
                    name={`${BASIC_PATH}.imageUrls`}
                    id={`${BASIC_PATH}.imageUrls`}
                    render={({ field }) => (
                      <MultiContentField
                        platform={GOOGLE}
                        language={language}
                        disabled={isDisabledFields}
                        acceptedFormats={GOOGLE_IMAGE_FORMATS}
                        fieldPath={`${BASIC_PATH}.imageUrls`}
                        mediaUrls={field.value}
                        showGalleryGoogleOption={true}
                        propsPostsAdsPlatforms={platformsData}
                        initialValuesAddContent={
                          creativeEntryImage
                            ? {
                                prompt:
                                  buildCreativeOptimizationPrompt(
                                    creativeEntryImage
                                  ),
                              }
                            : {}
                        }
                        onChange={(contents) => {
                          const newImageUrls = contents.map(
                            (content) => content.url
                          );
                          const newValues =
                            getValues(`${BASIC_PATH}.imageUrls`) || [];
                          field.onChange([...newValues, ...newImageUrls]);
                          if (creativeEntryImage?.slotKey) {
                            onRemoveCreativeOptimization(
                              creativeEntryImage.slotKey
                            );
                          }
                        }}
                        onDelete={(index) => {
                          field.onChange(
                            field.value.filter((_, i) => i !== index)
                          );
                        }}
                      />
                    )}
                  />
                </AssetsGroupMediaSection>
              </Grid>
              <Grid item {...grid}>
                <AssetsGroupMediaSection
                  showTooltip={showTooltipVideoUrls}
                  titleTooltip={creativeEntryVideo?.title}
                  actionTooltip={creativeEntryVideo?.action}
                  title={t(VIDEOS_OF_CAMPAIGN_PLACEHOLDER)}
                  helperText={t(
                    REQUIRED_VIDEOS_GOOGLE_ASSETS_GROUP_HELPER_TEXT
                  )}
                  name={`${BASIC_PATH}.videoUrls`}
                  id={`${BASIC_PATH}.videoUrls`}
                  errors={errors}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "videoUrls" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
                    </ClickableText>
                  </Box>
                  <UploadYoutubeVideoLink
                    name={`${BASIC_PATH}.videoUrls`}
                    disabled={isDisabledFields}
                    language={language}
                    maxVideos={ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP}
                    onAfterAppend={() => {
                      if (creativeEntryVideo?.slotKey) {
                        onRemoveCreativeOptimization(
                          creativeEntryVideo.slotKey
                        );
                      }
                    }}
                  />
                  <ContainerVideosYoutube
                    showUrlInput={false}
                    name={`${BASIC_PATH}.videoUrls`}
                    disabled={isDisabledFields}
                    maxVideos={ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP}
                  />
                </AssetsGroupMediaSection>
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>

        {/* Segmentation Configuration */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(ADS_CAMPAIGN_SEGMENTATION_SUB_TITLE)}
                </Typography>
              </Box>
            }
          >
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(SEARCH_THEMES_PLACEHOLDER)}
              </Typography>
              <SelectCreatable
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: BOX_SHADOW_FIELDS,
                  borderRadius: BORDER_RADIUS,
                }}
                limitTags={4}
                control={control}
                name={`${BASIC_PATH}.searchThemes`}
                errors={errors}
                fullWidth
                disabled={isDisabledFields}
              />
              <ClickableText
                disabled={isDisabledFields}
                onClick={() => onCopyValuesInOtherAds({ type: "searchThemes" })}
              >
                {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
              </ClickableText>
            </Grid>
          </CardContainer>
        </Grid>

        <Grid item xs={12}>
          <CustomAudiencesContainerGoogle
            name={`${BASIC_PATH}.customAudiences`}
            control={control}
            disabled={isDisabledFields}
            afterTitle={
              <ClickableText
                disabled={isDisabledFields}
                onClick={() =>
                  onCopyValuesInOtherAds({ type: "customAudiences" })
                }
              >
                {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
              </ClickableText>
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} id={`${BASIC_PATH}.longHeadlines`}>
          <CardContainerWithCopy
            title={t(LONG_HEADLINES)}
            disabled={isDisabledFields}
            hasCopyValue={true}
            onCopyCallback={() =>
              onCopyValuesInOtherAds({ type: "longHeadlines" })
            }
            id={`${BASIC_PATH}.longHeadlines`}
          >
            {longHeadlines.map((headline, index) => (
              <TextFieldWithActions
                key={headline.id}
                type="longHeadlines"
                labelTitle={t(LONG_HEADLINE_PLACEHOLDER) + " " + (index + 1)}
                name={`${BASIC_PATH}.longHeadlines.[${index}].value`}
                disabled={isDisabledFields}
                showAddIcon={
                  index === longHeadlines.length - 1 &&
                  index < ADS_CAMPAIGN_GOOGLE_MAX_LONG_HEADLINES - 1
                }
                showDeleteIcon={longHeadlines.length > 3}
                maxLength={
                  ADS_CAMPAIGN_PLATFORM_LONG_HEADLINE_MAX_LENGTH[platform]
                }
                index={index}
                platform={platform}
                onAdd={() => onAddText({ type: "longHeadlines" })}
                onRemove={() => onRemoveText({ type: "longHeadlines", index })}
                onGenerate={async () => {
                  await onGenerateText({ type: "longHeadlines", index });
                }}
                control={control}
                errors={errors}
              />
            ))}
          </CardContainerWithCopy>
        </Grid>

        <Grid item xs={12} sm={6} id={`${BASIC_PATH}.descriptions`}>
          <CardContainerWithCopy
            title={t(DESCRIPTIONS)}
            disabled={isDisabledFields}
            hasCopyValue={true}
            onCopyCallback={() =>
              onCopyValuesInOtherAds({ type: "descriptions" })
            }
            id={`${BASIC_PATH}.descriptions`}
          >
            {descriptions.map((description, index) => (
              <TextFieldWithActions
                key={description.id}
                labelTitle={t(DESCRIPTION_PLACEHOLDER) + " " + (index + 1)}
                type="descriptions"
                name={`${BASIC_PATH}.descriptions.[${index}].value`}
                disabled={isDisabledFields}
                platform={platform}
                showAddIcon={
                  index === descriptions.length - 1 &&
                  index < ADS_CAMPAIGN_GOOGLE_MAX_DESCRIPTIONS - 1
                }
                showDeleteIcon={descriptions.length > 3}
                firstItemMaxLength={
                  ADS_CAMPAIGN_GOOGLE_FIRST_DESCRIPTION_MAX_LENGTH
                }
                maxLength={
                  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[platform]
                }
                index={index}
                onAdd={() => onAddText({ type: "descriptions" })}
                onRemove={() => onRemoveText({ type: "descriptions", index })}
                onGenerate={async () => {
                  await onGenerateText({ type: "descriptions", index });
                }}
                control={control}
                errors={errors}
              />
            ))}
          </CardContainerWithCopy>
        </Grid>

        <Grid item xs={12} sm={6} id={`${BASIC_PATH}.titles`}>
          <CardContainerWithCopy
            title={t(TITLES)}
            disabled={isDisabledFields}
            hasCopyValue={true}
            onCopyCallback={() => onCopyValuesInOtherAds({ type: "titles" })}
            id={`${BASIC_PATH}.titles`}
          >
            {titles.map((title, index) => (
              <TextFieldWithActions
                labelTitle={t(TITLE_PLACEHOLDER) + " " + (index + 1)}
                key={title.id}
                type="titles"
                name={`${BASIC_PATH}.titles.[${index}].value`}
                disabled={isDisabledFields}
                showAddIcon={
                  index === titles.length - 1 &&
                  index < ADS_CAMPAIGN_GOOGLE_MAX_TITLES - 1
                }
                showDeleteIcon={titles.length > 3}
                platform={platform}
                onAdd={() => onAddText({ type: "titles" })}
                onRemove={() => onRemoveText({ type: "titles", index })}
                firstItemMaxLength={ADS_CAMPAIGN_GOOGLE_FIRST_TITLE_MAX_LENGTH}
                maxLength={ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]}
                index={index}
                onGenerate={async () => {
                  await onGenerateText({ type: "titles", index });
                }}
                control={control}
                errors={errors}
              />
            ))}
          </CardContainerWithCopy>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssetsGroupGoogleForm;
